import PublicHeader from '@/components/PublicHeader'
import PublicFooter from '@/components/PublicFooter'

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 text-gray-900 dark:text-white transition-colors">
      <PublicHeader />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="space-y-8 animate-fade-in">
          {/* Hero */}
          <section>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              Terms of Service
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              Last updated: {new Date().toLocaleDateString()}
            </p>
          </section>

          {/* Intro */}
          <section className="glass-card rounded-xl p-6 space-y-4">
            <p className="text-gray-700 dark:text-gray-300">
              Welcome to GistMag. These Terms of Service ("Terms") govern your access to and use of GistMag's
              services, including our website, API, and related tools. By using GistMag, you agree to be bound by
              these Terms. If you do not agree to these Terms, please do not use our services.
            </p>
          </section>

          {/* Acceptance */}
          <section className="glass-card rounded-xl p-6 space-y-3">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Acceptance of Terms
            </h2>
            <p className="text-gray-700 dark:text-gray-300">
              By creating an account, accessing our API, or using any GistMag service, you acknowledge that you have
              read, understood, and agree to be bound by these Terms and our Privacy Policy. If you are using GistMag
              on behalf of an organization, you represent that you have the authority to bind that organization to these
              Terms.
            </p>
          </section>

          {/* Description of Service */}
          <section className="glass-card rounded-xl p-6 space-y-3">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Description of Service
            </h2>
            <p className="text-gray-700 dark:text-gray-300">
              GistMag provides AI-powered tools and services, including but not limited to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
              <li>Alt text generation for images</li>
              <li>Text-to-speech (TTS) services</li>
              <li>Blog content generation tools</li>
              <li>API access for integration with third-party applications</li>
              <li>Browser extensions and WordPress plugins</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300">
              We reserve the right to modify, suspend, or discontinue any part of our services at any time, with or
              without notice.
            </p>
          </section>

          {/* User Accounts */}
          <section className="glass-card rounded-xl p-6 space-y-3">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              User Accounts and Registration
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
              <li>
                You must provide accurate, current, and complete information when creating an account.
              </li>
              <li>
                You are responsible for maintaining the confidentiality of your account credentials and API keys.
              </li>
              <li>
                You are responsible for all activities that occur under your account.
              </li>
              <li>
                You must notify us immediately of any unauthorized use of your account.
              </li>
            </ul>
          </section>

          {/* Acceptable Use */}
          <section className="glass-card rounded-xl p-6 space-y-3">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Acceptable Use
            </h2>
            <p className="text-gray-700 dark:text-gray-300">
              You agree not to use GistMag to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
              <li>Violate any applicable laws, regulations, or third-party rights.</li>
              <li>Generate content that is illegal, harmful, abusive, defamatory, or infringing on intellectual property rights.</li>
              <li>Attempt to reverse engineer, decompile, or extract the source code of our services.</li>
              <li>Interfere with or disrupt the integrity or performance of our services.</li>
              <li>Use automated systems (bots, scrapers) to access our API beyond reasonable rate limits.</li>
              <li>Share, resell, or redistribute your API key without authorization.</li>
              <li>Use our services to create competing products or services.</li>
            </ul>
          </section>

          {/* Billing and Payments */}
          <section className="glass-card rounded-xl p-6 space-y-3">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Billing and Payments
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
              <li>
                Subscription fees are billed in advance on a monthly or annual basis, as selected.
              </li>
              <li>
                Credits and usage are tracked according to our pricing tiers. Unused credits may expire according to
                your plan terms.
              </li>
              <li>
                All payments are processed securely through our payment processor. You agree to provide valid payment information.
              </li>
              <li>
                Refunds are handled on a case-by-case basis. Contact support for refund requests.
              </li>
              <li>
                We reserve the right to change our pricing with 30 days' notice to existing customers.
              </li>
              <li>
                Failure to pay may result in suspension or termination of your account.
              </li>
            </ul>
          </section>

          {/* Intellectual Property */}
          <section className="glass-card rounded-xl p-6 space-y-3">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Intellectual Property
            </h2>
            <p className="text-gray-700 dark:text-gray-300">
              GistMag and its original content, features, and functionality are owned by GistMag and are protected by
              international copyright, trademark, patent, trade secret, and other intellectual property laws.
            </p>
            <p className="text-gray-700 dark:text-gray-300">
              You retain ownership of content you submit to GistMag. By using our services, you grant us a
              non-exclusive, worldwide, royalty-free license to use, process, and store your content solely for the
              purpose of providing our services.
            </p>
            <p className="text-gray-700 dark:text-gray-300">
              Generated outputs (alt text, TTS audio, etc.) are provided to you for your use, subject to these Terms.
            </p>
          </section>

          {/* API Usage */}
          <section className="glass-card rounded-xl p-6 space-y-3">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              API Usage and Rate Limits
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
              <li>
                API access is subject to rate limits based on your subscription tier.
              </li>
              <li>
                You must not exceed your plan's rate limits or attempt to circumvent them.
              </li>
              <li>
                Excessive usage may result in temporary throttling or account suspension.
              </li>
              <li>
                We reserve the right to modify rate limits with reasonable notice.
              </li>
            </ul>
          </section>

          {/* Service Availability */}
          <section className="glass-card rounded-xl p-6 space-y-3">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Service Availability and Disclaimers
            </h2>
            <p className="text-gray-700 dark:text-gray-300">
              While we strive to provide reliable service, GistMag is provided "as is" and "as available." We do not
              guarantee that our services will be uninterrupted, error-free, or completely secure. We disclaim all
              warranties, express or implied, including but not limited to merchantability, fitness for a particular
              purpose, and non-infringement.
            </p>
          </section>

          {/* Limitation of Liability */}
          <section className="glass-card rounded-xl p-6 space-y-3">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Limitation of Liability
            </h2>
            <p className="text-gray-700 dark:text-gray-300">
              To the maximum extent permitted by law, GistMag shall not be liable for any indirect, incidental,
              special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred
              directly or indirectly, or any loss of data, use, goodwill, or other intangible losses resulting from your
              use of our services.
            </p>
          </section>

          {/* Termination */}
          <section className="glass-card rounded-xl p-6 space-y-3">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Termination
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
              <li>
                You may cancel your account at any time through the dashboard or by contacting support.
              </li>
              <li>
                We reserve the right to suspend or terminate your account if you violate these Terms.
              </li>
              <li>
                Upon termination, your right to use GistMag will immediately cease.
              </li>
              <li>
                We may delete your account data after a reasonable retention period following termination.
              </li>
            </ul>
          </section>

          {/* Changes to Terms */}
          <section className="glass-card rounded-xl p-6 space-y-3">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Changes to Terms
            </h2>
            <p className="text-gray-700 dark:text-gray-300">
              We reserve the right to modify these Terms at any time. We will notify users of material changes via
              email or through our dashboard. Your continued use of GistMag after such changes constitutes acceptance of
              the updated Terms.
            </p>
          </section>

          {/* Governing Law */}
          <section className="glass-card rounded-xl p-6 space-y-3">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Governing Law
            </h2>
            <p className="text-gray-700 dark:text-gray-300">
              These Terms shall be governed by and construed in accordance with the laws of the United Kingdom, without
              regard to its conflict of law provisions. Any disputes arising from these Terms or your use of GistMag
              shall be subject to the exclusive jurisdiction of the courts of the United Kingdom.
            </p>
          </section>

          {/* Contact */}
          <section className="glass-card rounded-xl p-6 space-y-3">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Contact Us
            </h2>
            <p className="text-gray-700 dark:text-gray-300">
              If you have any questions about these Terms of Service, please contact us at
              <span className="font-semibold"> support@gistmag.co.uk</span>.
            </p>
          </section>
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}
